package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.explain.DayExplanations
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayResults
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.LocalDaySpecials
import com.evoapps.evoshift.ui.util.LocalDayWeather
import com.evoapps.evoshift.ui.util.LocalStaffingHints
import com.evoapps.evoshift.ui.util.SpecialDayMarker
import com.evoapps.evoshift.ui.util.StaffingChip
import com.evoapps.evoshift.ui.util.WeatherBadge
import com.evoapps.evoshift.ui.util.formatPercentOrDash
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.comparison_metric_labour
import evoshift.composeapp.generated.resources.daily_records_month_summary
import evoshift.composeapp.generated.resources.daily_rows_day
import evoshift.composeapp.generated.resources.daily_rows_labour
import evoshift.composeapp.generated.resources.daily_rows_margin
import evoshift.composeapp.generated.resources.daily_rows_open_day
import evoshift.composeapp.generated.resources.daily_rows_plan
import evoshift.composeapp.generated.resources.daily_rows_result
import evoshift.composeapp.generated.resources.daily_rows_takings
import org.jetbrains.compose.resources.stringResource

/*
 * SH-228: the month's daily results on a phone in portrait (the owner found the day cards too
 * long to scan): the month so far stays on top while the days scroll under it, then one compact
 * row per day — the day and weekday with its weather, special day and staffing mark, the takings,
 * the labour share and the result in green or red. A tap opens the day's sheet (EditActualsDialog)
 * with every figure, the takings to enter and the target. Wider windows keep the table.
 */

/** Test tag of the phone's list of day rows. */
const val DAILY_DAY_ROWS_TAG = "daily_records_day_rows"

/** Test tag of one day's row ([date]: ISO). */
fun dailyDayRowTag(date: String) = "daily_records_day_row_$date"

/** The columns' weights: the day, the takings, the labour share, the result. */
private const val DAY_WEIGHT = 1.2f
private const val TAKINGS_WEIGHT = 1.2f
private const val LABOUR_WEIGHT = 0.95f
private const val RESULT_WEIGHT = 1.2f

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun DailyRecordsDayList(
    records: List<DailyRecord>,
    currency: String,
    onRecordClick: (DailyRecord) -> Unit,
    bottomPadding: Dp = 8.dp
) {
    val resultDay = LocalResultDay.current
    LazyColumn(
        modifier = Modifier.fillMaxSize().testTag(DAILY_DAY_ROWS_TAG),
        contentPadding = PaddingValues(bottom = bottomPadding)
    ) {
        if (records.isNotEmpty()) {
            // The month so far stays on top while the days scroll under it.
            stickyHeader(key = "month") {
                Column(Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surface)) {
                    CompactMonthSummary(records, currency)
                    DayRowsHeader()
                    HorizontalDivider()
                }
            }
        }
        items(records, key = { it.date }) { record ->
            DailyDayRow(resultDay(record), currency, onClick = { onRecordClick(record) })
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
        }
    }
}

/**
 * The month so far in two lines: the result and margin, the takings and the labour share, each
 * actual (the days with takings) with the plan (the whole month) beside it. The result opens its
 * revenue and cost lines.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CompactMonthSummary(records: List<DailyRecord>, currency: String) {
    val resultDay = LocalResultDay.current
    val days = remember(records, resultDay) { records.map(resultDay) }
    val plan = remember(days) { DayResults.plan(days) }
    val actual = remember(days) { DayResults.actual(days) }
    val figures = remember(records) { monthFigures(records) }
    val muted = MaterialTheme.colorScheme.onSurfaceVariant
    Column(Modifier.fillMaxWidth().testTag(DAILY_MONTH_SUMMARY_TAG).padding(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 6.dp)) {
        Text(stringResource(Res.string.daily_records_month_summary), style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
        FlowRow(
            Modifier.fillMaxWidth().padding(top = 2.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
            maxItemsInEachRow = 2
        ) {
            val cell = Modifier.weight(1f)
            SummaryCell(
                label = stringResource(Res.string.daily_rows_result),
                actual = if (actual.days > 0) formatMoney(actual.result, currency) else "-",
                plan = formatMoney(plan.result, currency),
                actualColor = if (actual.days > 0) dayResultColor(actual.result) else Color.Unspecified,
                explain = { DayExplanations.resultOfDays(days, Side.ACTUAL) }.takeIf { actual.days > 0 },
                currency = currency,
                modifier = cell
            )
            SummaryCell(
                label = stringResource(Res.string.daily_rows_margin),
                actual = formatPercentOrDash(actual.percent.takeIf { actual.days > 0 }),
                plan = formatPercentOrDash(plan.percent),
                modifier = cell
            )
            SummaryCell(
                label = stringResource(Res.string.daily_rows_takings),
                actual = if (actual.days > 0) formatMoney(figures.salesActual, currency) else "-",
                plan = formatMoney(figures.salesPlan, currency),
                modifier = cell
            )
            SummaryCell(
                label = stringResource(Res.string.comparison_metric_labour),
                actual = formatPercentOrDash(figures.labourPercentActual),
                plan = formatPercentOrDash(figures.labourPercentPlan),
                modifier = cell
            )
        }
        Spacer(Modifier.size(2.dp))
        Text(stringResource(Res.string.daily_rows_plan), style = MaterialTheme.typography.labelSmall, color = muted)
    }
}

/** A label, the actual figure (bold) and the plan beside it (muted). */
@Composable
private fun SummaryCell(
    label: String,
    actual: String,
    plan: String,
    modifier: Modifier = Modifier,
    actualColor: Color = Color.Unspecified,
    explain: (() -> com.evoapps.evoshift.domain.explain.Calculation?)? = null,
    currency: String = ""
) {
    val muted = MaterialTheme.colorScheme.onSurfaceVariant
    Column(modifier) {
        Text(label, style = MaterialTheme.typography.labelSmall, color = muted, maxLines = 1, overflow = TextOverflow.Ellipsis)
        Row(verticalAlignment = Alignment.Bottom) {
            ExplainableAmount(
                actual, explain = explain, currency = currency,
                style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = actualColor, maxLines = 1
            )
            Spacer(Modifier.width(6.dp))
            Text(plan, style = MaterialTheme.typography.labelSmall, color = muted, maxLines = 1, softWrap = false)
        }
    }
}

/** The columns' names over the rows. */
@Composable
private fun DayRowsHeader() {
    val style = MaterialTheme.typography.labelSmall
    val muted = MaterialTheme.colorScheme.onSurfaceVariant
    Row(Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)) {
        Text(stringResource(Res.string.daily_rows_day), Modifier.weight(DAY_WEIGHT), style = style, color = muted)
        Text(stringResource(Res.string.daily_rows_takings), Modifier.weight(TAKINGS_WEIGHT), style = style, color = muted, textAlign = TextAlign.End)
        Text(stringResource(Res.string.daily_rows_labour), Modifier.weight(LABOUR_WEIGHT), style = style, color = muted, textAlign = TextAlign.End)
        Text(stringResource(Res.string.daily_rows_result), Modifier.weight(RESULT_WEIGHT), style = style, color = muted, textAlign = TextAlign.End)
    }
}

/**
 * One day: "22 (Cz)" with its weather, special day and staffing mark under it, then the takings,
 * the labour share and the result (green or red); dashes until the takings are in. One tap
 * target that opens the day.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun DailyDayRow(record: DailyRecord, currency: String, onClick: () -> Unit) {
    val hasTakings = record.revenue.realNetto != null
    val muted = MaterialTheme.colorScheme.onSurfaceVariant
    val label = dayLabel(record)
    Row(
        Modifier
            .fillMaxWidth()
            .background(if (record.date in LocalRestDays.current) RestDayTint else Color.Transparent)
            .handCursor()
            .clickable(onClickLabel = stringResource(Res.string.daily_rows_open_day, label), role = Role.Button, onClick = onClick)
            .testTag(dailyDayRowTag(record.date))
            .padding(horizontal = 16.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(DAY_WEIGHT)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(label, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, maxLines = 1)
                if (record.isLocked) {
                    Spacer(Modifier.width(4.dp))
                    Icon(Icons.Default.Lock, contentDescription = null, modifier = Modifier.size(12.dp), tint = muted)
                }
            }
            CompositionLocalProvider(LocalContentColor provides muted) {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), itemVerticalAlignment = Alignment.CenterVertically) {
                    LocalDayWeather.current[record.date]?.let { WeatherBadge(it, markForecast = true) }
                    LocalDaySpecials.current[record.date]?.let { SpecialDayMarker(it, maxWidth = 72.dp) }
                    ClosedDayNote(record.date, short = true)
                    if (!hasTakings) LocalStaffingHints.current[record.date]?.let { StaffingChip(it) }
                }
            }
        }
        Text(
            record.revenue.realNetto?.let { formatMoney(it, currency) } ?: "-",
            Modifier.weight(TAKINGS_WEIGHT), style = MaterialTheme.typography.bodyMedium,
            fontWeight = if (hasTakings) FontWeight.SemiBold else FontWeight.Normal,
            color = if (hasTakings) Color.Unspecified else muted, textAlign = TextAlign.End, maxLines = 1
        )
        Text(
            if (hasTakings) formatPercentOrDash(record.labourPercentReal) else "-",
            Modifier.weight(LABOUR_WEIGHT), style = MaterialTheme.typography.bodyMedium,
            color = if (hasTakings) Color.Unspecified else muted, textAlign = TextAlign.End, maxLines = 1
        )
        Text(
            if (hasTakings) formatMoney(record.resultReal, currency) else "-",
            Modifier.weight(RESULT_WEIGHT), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold,
            color = if (hasTakings) dayResultColor(record.resultReal) else muted, textAlign = TextAlign.End, maxLines = 1
        )
    }
}
